import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Check, RotateCcw, X } from "lucide-react";
import { useContentStore, getItemMeta, type ContentItem } from "@/stores/contentStore";
import { useToast } from "@/hooks/use-toast";

interface ContentReviewPanelProps {
  item: ContentItem;
  projectId: number;
}

export function ContentReviewPanel({ item, projectId }: ContentReviewPanelProps) {
  const submitReview = useContentStore((s) => s.submitReview);
  const { toast } = useToast();
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const revisionCount = item.revision_count ?? 0;
  const maxRevisions = 3;

  const handleAction = async (action: "approve" | "revise" | "kill") => {
    if (action === "revise" && !showFeedback) {
      setShowFeedback(true);
      return;
    }

    setSubmitting(true);
    try {
      await submitReview(item.id, projectId, {
        action,
        feedback: action === "revise" ? feedback : undefined,
      });
      toast({ title: action === "approve" ? "Article approved" : action === "revise" ? "Revision requested" : "Article killed" });
    } catch {
      toast({ title: "Review failed", variant: "destructive" });
    } finally {
      setSubmitting(false);
      setShowFeedback(false);
      setFeedback("");
    }
  };

  return (
    <div className="border border-amber-500/30 bg-amber-500/5 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-amber-400">REVIEW MODE</span>
        <span className="text-xs text-muted-foreground">
          {item.word_count.toLocaleString()} words
        </span>
      </div>

      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          className="border-green-500/30 text-green-400 hover:bg-green-500/10"
          onClick={() => handleAction("approve")}
          disabled={submitting}
        >
          <Check className="w-4 h-4 mr-1" /> Approve
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
          onClick={() => handleAction("revise")}
          disabled={submitting || revisionCount >= maxRevisions}
        >
          <RotateCcw className="w-4 h-4 mr-1" /> Revise
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
          onClick={() => handleAction("kill")}
          disabled={submitting}
        >
          <X className="w-4 h-4 mr-1" /> Kill
        </Button>
      </div>

      {showFeedback && (
        <div className="space-y-2">
          <label htmlFor="review-feedback" className="sr-only">Revision feedback</label>
          <Textarea
            id="review-feedback"
            placeholder="What should be improved?"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={3}
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              Revision {revisionCount + 1} of max {maxRevisions}
            </span>
            <Button
              size="sm"
              onClick={() => handleAction("revise")}
              disabled={!feedback.trim() || submitting}
            >
              Submit Feedback
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
